/**
 * Audio Player (TTS)
 *
 * Parses narrative text with voice tags, generates audio via ElevenLabs,
 * and plays back with voice switching and intonation control.
 */

import { VOICES, NPC_TYPE_VOICES } from "./voices";
import { INTONATION_TAGS } from "./intonation-tags";

export type PlaybackState = "idle" | "loading" | "playing" | "paused";

interface Segment {
  type: "narration" | "dialogue" | "sfx";
  text: string;
  voice: string;
  intonation?: string;
  sfxId?: string;
}

export interface AudioPlayerEvents {
  onStateChange: (state: PlaybackState) => void;
  onProgress: (current: number, total: number) => void;
  onComplete: () => void;
  onDebugLog?: (type: "request" | "response" | "error" | "info", message: string, data?: any) => void;
}

export class AudioPlayer {
  private proxyUrl: string;
  private ctx: AudioContext | null = null;
  private gain: GainNode | null = null;
  private source: AudioBufferSourceNode | null = null;
  private segments: Segment[] = [];
  private buffers = new Map<number, AudioBuffer>();
  private index = 0;
  private state: PlaybackState = "idle";
  private muted = false;
  private volume = 1;
  private speed = 1.15;
  private events: Partial<AudioPlayerEvents> = {};
  private sfxFn: ((id: string) => void) | null = null;

  constructor(proxyUrl: string) {
    this.proxyUrl = proxyUrl;
  }

  setEventHandlers(events: Partial<AudioPlayerEvents>): void {
    this.events = events;
  }

  setSfxPlayer(fn: (id: string) => void): void {
    this.sfxFn = fn;
  }

  setSpeed(speed: number): void {
    this.speed = Math.max(0.5, Math.min(2, speed));
  }

  getState(): PlaybackState {
    return this.state;
  }

  isMuted(): boolean {
    return this.muted;
  }

  private setState(state: PlaybackState): void {
    this.state = state;
    this.events.onStateChange?.(state);
  }

  /**
   * Parse narrative into segments with voice/intonation tags
   */
  parse(text: string): Segment[] {
    const segments: Segment[] = [];
    let voice = "narrator";
    let intonation: string | undefined;

    // Clean markdown formatting
    const clean = text
      .replace(/\*\*([^*]+)\*\*/g, "$1")
      .replace(/\*([^*]+)\*/g, "$1");

    this.events.onDebugLog?.("info", "TTS: Starting parse", {
      originalLength: text.length,
      cleanedLength: clean.length,
      preview: clean.substring(0, 200),
    });

    // Pattern to match all tags (voice tags, intonation tags, SFX tags, and all closing tags)
    // IMPORTANT: Use non-capturing groups (?:...) to prevent tag names from appearing as separate elements
    const pattern =
      /(\[SFX:[^\]]+\]|\[NPC:[^\]]+\]|\[NARRATOR\]|\[COMPUTER\]|\[PA\]|\[SARCASM\]|\[PANIC\]|\[WHISPER\]|\[DRAMATIC\]|\[DEADPAN\]|\[CHEERFUL\]|\[THREATENING\]|\[EXHAUSTED\]|\[EXCITED\]|\[NERVOUS\]|\[SMUG\]|\[\/(?:NARRATOR|COMPUTER|PA|NPC|SARCASM|PANIC|WHISPER|DRAMATIC|DEADPAN|CHEERFUL|THREATENING|EXHAUSTED|EXCITED|NERVOUS|SMUG)\])/gi;

    const parts = clean.split(pattern).filter(Boolean);

    this.events.onDebugLog?.("info", `TTS: Split into ${parts.length} parts`, {
      parts: parts.map((p, i) => ({ index: i, preview: p.substring(0, 50), length: p.length })),
    });

    for (const part of parts) {
      const trimmed = part.trim();
      if (!trimmed) {
        this.events.onDebugLog?.("info", "TTS: Skipping empty part");
        continue;
      }

      // SFX tag
      const sfx = trimmed.match(/\[SFX:([^\]]+)\]/);
      if (sfx) {
        segments.push({ type: "sfx", text: "", voice, sfxId: sfx[1] });
        continue;
      }

      // Voice switching
      if (trimmed === "[NARRATOR]") {
        voice = "narrator";
        continue;
      }
      if (trimmed === "[COMPUTER]") {
        voice = "narrator";
        intonation = "deadpan";
        continue;
      }
      if (trimmed === "[PA]") {
        voice = "station_pa";
        intonation = "deadpan";
        continue;
      }

      // NPC voice
      const npc = trimmed.match(/\[NPC:([^\]]+)\]/);
      if (npc) {
        voice =
          NPC_TYPE_VOICES[npc[1].toLowerCase()] || "npc_male_gruff";
        continue;
      }

      // Intonation start tags
      const tag = trimmed.match(/^\[([A-Z]+)\]$/i);
      if (tag && INTONATION_TAGS[tag[1].toLowerCase()]) {
        intonation = tag[1].toLowerCase();
        continue;
      }

      // Voice closing tags (reset to narrator)
      if (/^\[\/(?:NARRATOR|COMPUTER|PA|NPC)\]$/i.test(trimmed)) {
        voice = "narrator";
        intonation = undefined;
        continue;
      }

      // Intonation closing tags (clear intonation but keep voice)
      if (/^\[\/(?:SARCASM|PANIC|WHISPER|DRAMATIC|DEADPAN|CHEERFUL|THREATENING|EXHAUSTED|EXCITED|NERVOUS|SMUG)\]$/i.test(trimmed)) {
        intonation = undefined;
        continue;
      }

      // Text segment
      this.events.onDebugLog?.("info", `TTS: Adding text segment`, {
        segmentIndex: segments.length,
        type: voice === "narrator" ? "narration" : "dialogue",
        voice,
        intonation,
        textPreview: trimmed.substring(0, 100),
        textLength: trimmed.length,
      });

      segments.push({
        type: voice === "narrator" ? "narration" : "dialogue",
        text: trimmed,
        voice,
        intonation,
      });
    }

    this.events.onDebugLog?.("info", `TTS: Parse complete - ${segments.length} segments created`);

    return segments;
  }

  /**
   * Load all audio segments
   */
  async load(narrative: string): Promise<void> {
    this.segments = this.parse(narrative);
    this.buffers.clear();
    this.index = 0;
    this.setState("loading");

    this.events.onDebugLog?.("info", `TTS: Parsed ${this.segments.length} segments`, {
      segments: this.segments.map((seg, i) => ({
        index: i + 1,
        type: seg.type,
        voice: seg.voice,
        intonation: seg.intonation,
        textPreview: seg.text.substring(0, 30) + (seg.text.length > 30 ? "..." : ""),
        sfxId: seg.sfxId,
      })),
    });

    if (!this.ctx) {
      this.ctx = new AudioContext();
      this.gain = this.ctx.createGain();
      this.gain.connect(this.ctx.destination);
    }

    const promises = this.segments.map(async (seg, i) => {
      if (seg.type === "sfx") return { i, buf: null };

      const voiceConfig = VOICES[seg.voice] || VOICES.narrator;
      const intonationConfig = seg.intonation
        ? INTONATION_TAGS[seg.intonation]
        : null;

      const requestBody = {
        text: seg.text,
        voiceId: voiceConfig.id,
        settings: {
          stability:
            intonationConfig?.stability ?? voiceConfig.settings.stability,
          similarity_boost: voiceConfig.settings.similarity_boost,
          style: intonationConfig?.style ?? voiceConfig.settings.style ?? 0,
        },
      };

      this.events.onDebugLog?.(
        "request",
        `TTS: Generating segment ${i + 1}/${this.segments.length}`,
        {
          url: `${this.proxyUrl}/tts`,
          method: "POST",
          voice: seg.voice,
          voiceId: voiceConfig.id,
          intonation: seg.intonation,
          textLength: seg.text.length,
          textPreview: seg.text.substring(0, 100),
          settings: requestBody.settings,
        }
      );

      try {
        const res = await fetch(`${this.proxyUrl}/tts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        this.events.onDebugLog?.(
          "response",
          `TTS: Response for segment ${i + 1} (${res.status})`,
          {
            status: res.status,
            statusText: res.statusText,
            ok: res.ok,
            contentType: res.headers.get("content-type"),
          }
        );

        if (!res.ok) {
          const errorText = await res.text();
          this.events.onDebugLog?.(
            "error",
            `TTS: Failed for segment ${i + 1} (${res.status})`,
            {
              status: res.status,
              statusText: res.statusText,
              error: errorText,
            }
          );
          return { i, buf: null };
        }

        const arrayBuffer = await res.arrayBuffer();
        this.events.onDebugLog?.(
          "response",
          `TTS: Audio decoded for segment ${i + 1}`,
          {
            audioSize: arrayBuffer.byteLength,
          }
        );

        // Safety check: AudioContext might be null if browser suspended it
        if (!this.ctx) {
          this.events.onDebugLog?.(
            "error",
            `TTS: AudioContext is null, cannot decode audio for segment ${i + 1}`,
            { note: "Browser may have suspended AudioContext. Try user gesture to resume." }
          );
          return { i, buf: null };
        }

        const buf = await this.ctx.decodeAudioData(arrayBuffer);
        return { i, buf };
      } catch (err) {
        this.events.onDebugLog?.(
          "error",
          `TTS: Exception for segment ${i + 1}`,
          {
            error: err instanceof Error ? err.message : String(err),
            stack: err instanceof Error ? err.stack : undefined,
          }
        );
        return { i, buf: null };
      }
    });

    for (const { i, buf } of await Promise.all(promises)) {
      if (buf) this.buffers.set(i, buf);
    }

    this.setState("idle");
  }

  /**
   * Play audio from current position
   */
  async play(): Promise<void> {
    // Prevent double-play - check state first
    if (this.state === "playing" || this.state === "loading") return;

    // Handle resume from paused state
    if (this.state === "paused" && this.ctx) {
      this.setState("playing");
      await this.ctx.resume();
      return;
    }

    // Set state immediately to prevent race conditions
    this.setState("playing");
    await this.playFrom(this.index);
  }

  private async playFrom(start: number): Promise<void> {
    if (this.ctx?.state === "suspended") await this.ctx.resume();

    this.events.onDebugLog?.("info", `TTS: Starting playback from segment ${start + 1}/${this.segments.length}`);

    for (let i = start; i < this.segments.length; i++) {
      if (this.state !== "playing") break;
      this.index = i;
      const seg = this.segments[i];

      this.events.onProgress?.(i, this.segments.length);

      // SFX
      if (seg.type === "sfx" && seg.sfxId) {
        this.events.onDebugLog?.("info", `TTS: Playing SFX segment ${i + 1}: ${seg.sfxId}`);
        this.sfxFn?.(seg.sfxId);
        await this.delay(400);
        continue;
      }

      // Voice audio
      const buf = this.buffers.get(i);
      if (!buf) {
        this.events.onDebugLog?.("error", `TTS: Skipping segment ${i + 1} - no audio buffer`, {
          voice: seg.voice,
          textPreview: seg.text.substring(0, 50),
        });
        continue;
      }

      this.events.onDebugLog?.("info", `TTS: Playing segment ${i + 1}/${this.segments.length}`, {
        voice: seg.voice,
        intonation: seg.intonation,
        textPreview: seg.text.substring(0, 50),
      });

      const intonationConfig = seg.intonation
        ? INTONATION_TAGS[seg.intonation]
        : null;
      const rate = this.speed * (intonationConfig?.speed ?? 1);
      const segmentVolume = intonationConfig?.volume ?? 1.0;

      await new Promise<void>((resolve) => {
        if (this.state !== "playing") {
          resolve();
          return;
        }

        this.source = this.ctx!.createBufferSource();
        this.source.buffer = buf;
        this.source.playbackRate.value = rate;

        // Apply segment-specific volume for intonation effects (e.g., whisper)
        if (segmentVolume !== 1.0) {
          const segmentGain = this.ctx!.createGain();
          segmentGain.gain.value = segmentVolume;
          this.source.connect(segmentGain);
          segmentGain.connect(this.gain!);
        } else {
          this.source.connect(this.gain!);
        }

        this.source.onended = () => {
          this.source = null;
          resolve();
        };
        this.source.start();
      });

      if (this.state === "playing") await this.delay(100);
    }

    if (this.state === "playing") {
      this.setState("idle");
      this.index = 0;
      this.events.onComplete?.();
    }
  }

  pause(): void {
    if (this.state !== "playing") return;
    this.ctx?.suspend();
    this.setState("paused");
  }

  stop(): void {
    this.source?.stop();
    this.source = null;
    this.index = 0;
    this.setState("idle");
  }

  toggleMute(): boolean {
    this.muted = !this.muted;
    if (this.gain) this.gain.gain.value = this.muted ? 0 : this.volume;
    return this.muted;
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.gain && !this.muted) this.gain.gain.value = this.volume;
  }

  dispose(): void {
    this.stop();
    this.ctx?.close();
    this.ctx = null;
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
